import "react-international-phone/style.css";
import { appendPatientTimezone } from "~/utils/patient-timezone";

import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput, DatePickerInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  ActionFunctionArgs,
  createCookie,
  HeadersArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import {
  Form,
  json,
  Link,
  redirect,
  useFetcher,
  useLoaderData,
  useRouteLoaderData,
} from "@remix-run/react";
import {
  IconArrowLeft,
  IconAt,
  IconCalendar,
  IconDiscountCheckFilled,
  IconExclamationCircle,
  IconInfoCircle,
  IconRosetteDiscount,
} from "@tabler/icons-react";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { PhoneInput } from "react-international-phone";
import { z } from "zod";

import { ConsultationServiceSelect } from "~/components/consultation-service-select";
import { PhoneNumberVerificationModal } from "~/components/phone-number-verification-modal";
import { Trans } from "~/components/typed-trans";
import { LARUCHE_IDENTIFIER, PAGE_TITLE } from "~/constants/index";
import { getGenderValues } from "~/data/dropdown-values";
import { useCountdown } from "~/hooks/use-countdown";
import { useProlongedNavigationState } from "~/hooks/use-prolonged-navigation-state";
import { useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import { loader as rootLoader } from "~/root";
import sharedClasses from "~/routes/($lang)._index/book-doctor.module.scss";
import { getDoctorAsapServices } from "~/services/get-doctor-asap-services";
import { getPartnerProfileBySlug } from "~/services/get-partner-profile-by-slug";
import utilityClasses from "~/styles/utilities.module.scss";
import { ComboboxItemPrice, OkDataError } from "~/types";
import { appendAccountAttribution } from "~/utils/account-engagement.client";
import { api } from "~/utils/api.server";
import { cleanPhoneNumber } from "~/utils/clean-phone-number";
import { resolveCurrencyContext } from "~/utils/currency.server";

import classes from "./book-doctor-asap.module.scss";

// i18next.t() no longer works for some reason, so I'm passing t() from the render function
const schema = (t: any) =>
  z
    .object({
      serviceType: z
        .string()
        .nullable() // Start as null
        .refine(val => val !== null, {
          // Don't allow form to be submitted if service type is null
          message: t("service-type-required"),
        }),
      fullName: z.string().min(1, t("full-name-required")),
      dob: z
        .date()
        .nullable() // Start as null
        .refine(val => val !== null, {
          // Don't allow form to be submitted if dob is null
          message: t("dob-required"),
        }),
      gender: z
        .string()
        .nullable() // Start as null
        .refine(val => val !== null, {
          // Don't allow form to be submitted if gender is null
          message: t("gender-required"),
        }),
      phoneNumber: z.string(),
      whatsappPhoneNumber: z.string(),
      additionalInformation: z
        .string()
        .min(1, t("appointment-purpose-required")),
      // Extra fields that are in the form but not validated
      serviceDate: z.union([z.string(), z.date()]).nullable().optional(),
      serviceTime: z.string().optional(),
      language: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // Only validate the field that should be filled
      const phoneNumberHasContent =
        data.phoneNumber && data.phoneNumber.trim().length > 4;
      const isPhoneNumberDisplayed = data.phoneNumber.trim().length > 0;
      const whatsappHasContent =
        data.whatsappPhoneNumber && data.whatsappPhoneNumber.trim().length > 4;
      const isWhatsappNumberDisplayed =
        data.whatsappPhoneNumber.trim().length > 0;

      if (!phoneNumberHasContent && isPhoneNumberDisplayed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("at-least-one-phone-number-required"),
          path: ["phoneNumber"],
        });
        return;
      }
      if (!whatsappHasContent && isWhatsappNumberDisplayed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("at-least-one-phone-number-required"),
          path: ["whatsappPhoneNumber"],
        });
        return;
      }

      // If phone number has content but is too short, validate it
      if (phoneNumberHasContent && data?.phoneNumber?.trim()?.length < 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("phone-number-too-short"),
          path: ["phoneNumber"],
        });
      }

      // If whatsapp has content but is too short, validate it
      if (
        whatsappHasContent &&
        data?.whatsappPhoneNumber?.trim()?.length < 11
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("phone-number-too-short"),
          path: ["whatsappPhoneNumber"],
        });
      }
    });

type Inputs = z.infer<ReturnType<typeof schema>>;

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data?.title },
    { property: "og:title", content: data?.title },
    { property: "og:description", content: data?.description ?? "" },
    { name: "description", content: data?.description ?? "" },
    { name: "robots", content: "noindex, nofollow" },
    {
      name: "keywords",
      content:
        "Doctor now, Docteur maintenant, Francophone Africa, African diaspora, Appointment, Rendez-vous, Teleconsultation, Téléconsultation",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: `${data?.origin}/${data?.locale}/doctor/asap`,
    },
    {
      tagName: "link",
      rel: "alternate",
      hrefLang: "en",
      href: `${data?.origin}/en/doctor/asap`,
    },
    {
      tagName: "link",
      rel: "alternate",
      hrefLang: "fr",
      href: `${data?.origin}/fr/doctor/asap`,
    },
    {
      tagName: "link",
      rel: "alternate",
      hrefLang: "x-default",
      href: `${data?.origin}/fr/doctor/asap`,
    },
  ];
};

export const headers = ({ actionHeaders, loaderHeaders }: HeadersArgs) => {
  if (loaderHeaders) {
    return loaderHeaders;
  }
  // Otherwise return action headers
  return actionHeaders;
};

export default function BookAppointmentWithDoctor() {
  const { t, i18n } = useTranslation();

  // console.log("data:", data, "ok:", ok, "error:", error);

  const emailRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [promoCode, setPromoCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [isPromoCodeValid, setIsPromoCodeValid] = useState("");
  const {
    isNavigating: isSubmitting,
    setIsNavigating: setIsSubmitting,
    reset: resetIsSubmitting,
  } = useProlongedNavigationState();
  const [serviceType, setServiceType] = useState<ComboboxItemPrice | null>(
    null
  );
  const [
    phoneNumberVerificationModalOpened,
    {
      open: openPhoneNumberVerificationModal,
      close: closePhoneNumberVerificationModal,
    },
  ] = useDisclosure(false);
  const [isAllowedToBook, setIsAllowedToBook] = useState(false);
  const { timeLeft, isComplete, reset: resetTimer } = useCountdown(90, true);
  const [noWhatsappNumber, setNoWhatsappNumber] = useState(false);

  const actionFetcher = useFetcher<typeof action>();
  const promoCodeFetcher = useFetcher<
    OkDataError<{
      isValid: boolean;
      discountPercent: number;
      services: string[];
    }>
  >();
  const loaderData = useLoaderData<typeof loader>();
  const servicesData = loaderData.services?.data as ComboboxItemPrice[];
  // console.log(servicesData);

  const form = useForm({
    // Controlled mode allows Select to properly work when changing language and also allows service time disabling logic to work properly when service date is changed
    mode: "controlled",
    validate: zodResolver(schema(t)),
    initialValues: {
      serviceType: null as string | null,
      fullName: "",
      dob: null as Date | null,
      gender: null as string | null,
      phoneNumber: "",
      whatsappPhoneNumber: "",
      serviceDate: null as Date | null | string,
      serviceTime: "",
      language: i18n.language,
      additionalInformation: "",
    },
  });

  const sourceId = loaderData.source;

  // useLogger("form", [form.getValues()]);

  const handleFormSubmit = (values: any) => {
    import.meta.env.DEV && console.log("[DEV] ", values);
    openPhoneNumberVerificationModal();
  };

  useEffect(() => {
    if (isAllowedToBook) {
      const formData = new FormData();
      appendPatientTimezone(formData);
      Object.entries(form.getValues()).forEach(([key, value]) => {
        formData.append(key, value?.toString() ?? "");
      });

      formData.append("cost", serviceType?.originalPrice?.toString() ?? "");
      formData.append("serviceTypeLabel", serviceType?.label ?? "");
      formData.append("promoCode", promoCode);
      formData.append("discountPercent", discountPercent?.toString() || "0");
      formData.append("timeZone", new Date().toISOString()); // Just needed to send timezone offset to backend
      if (sourceId) {
        formData.append("sourceId", sourceId);
      }

      const params = new URLSearchParams(window.location.search);
      const partnerSlug = params.get("partnerSlug");
      if (partnerSlug) {
        formData.append("partnerSlug", partnerSlug);
      }
      appendAccountAttribution(formData);

      actionFetcher.submit(formData, {
        method: "post",
        action: `/${i18n.language}/doctor/asap`,
      });
      setIsAllowedToBook(false);
    }
  }, [isAllowedToBook]);

  useEffect(() => {
    if (promoCodeFetcher.data?.data) {
      const services = promoCodeFetcher.data.data?.services;
      const isValid = promoCodeFetcher.data.data?.isValid;
      // console.log(isValid, services);
      if (!services?.length && isValid) {
        // Repeating this block of code intentionally - Apply promocode to all services
        setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
        setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      } else {
        setIsPromoCodeValid("false");
      }
    }
  }, [promoCodeFetcher.data]);

  useEffect(() => {
    if (actionFetcher.state === "submitting") {
      setIsSubmitting(true);
    }
    // Only reset when both the form is no longer submitting
    // AND we've reached a new location (redirect completed)
  }, [actionFetcher.state]);

  useEffect(() => {
    // console.log(actionFetcher.data?.error);
    if (actionFetcher.data?.error) {
      resetIsSubmitting();
    }
  }, [actionFetcher.data]);

  useEffect(() => {
    const serviceTypeValue = form.getValues().serviceType;
    // console.log("service type:", serviceTypeValue);
    if (serviceTypeValue && servicesData) {
      setServiceType(
        servicesData?.find(
          (item: any) => item.value === serviceTypeValue
        ) as ComboboxItemPrice
      );
    }
  }, [form.getValues().serviceType, servicesData]);

  useEffect(() => {
    if (noWhatsappNumber) {
      form.setFieldValue("whatsappPhoneNumber", "");
    } else {
      form.setFieldValue("phoneNumber", "");
    }
  }, [noWhatsappNumber]);

  return (
    <div className={classes.section}>
      <div className={utilityClasses["small-container"]}>
        <Form
          ref={formRef}
          method="post"
          className={sharedClasses.body}
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              import.meta.env.DEV && console.log("[DEV] form errors:", errors);
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <Stack justify="start">
            <Button
              w="fit-content"
              component={Link}
              to={`/${i18n.language}`}
              reloadDocument
              type="button"
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={16} />}>
              {t("asap-back-to-doctors")}
            </Button>
          </Stack>

          <Title
            order={1}
            fw={600}
            fz={{ base: 22, sm: 26 }}
            mt={"2rem"}
            ta={"center"}
            className={classes.heading}>
            {t("talk-to-doctor-immediately")}
          </Title>

          <Text ta="center" c="dimmed" fz={{ base: 14, sm: 15 }} mt="0.75rem">
            {t("asap-page-intro")}
          </Text>

          <section className={classes["asap-summary"]}>
            <Title order={2} fz={16} fw={600} ta="center">
              {t("asap-how-it-works")}
            </Title>
            <div className={classes["steps-grid"]}>
              <div className={classes.step}>
                <span>1</span>
                <Text fz={13} fw={500}>
                  {t("asap-step-need")}
                </Text>
              </div>
              <div className={classes.step}>
                <span>2</span>
                <Text fz={13} fw={500}>
                  {t("asap-step-verify")}
                </Text>
              </div>
              <div className={classes.step}>
                <span>3</span>
                <Text fz={13} fw={500}>
                  {t("asap-step-response")}
                </Text>
              </div>
            </div>
          </section>

          <Title order={2} fw={500} fz={18} mt={"2.5rem"}>
            {t("asap-consultation-request")}
          </Title>

          <ConsultationServiceSelect
            // disabled={!ok?.doctorProfile}
            // rightSection={
            //   error?.doctorProfileData ? (
            //     <IconExclamationCircle color="red" size={20} />
            //   ) : null
            // }
            mt={"3rem"}
            withAsterisk
            label={t("service-type")}
            {...form.getInputProps("serviceType")}
            key={form.key("serviceType")}
            data={servicesData?.map((item: any) => ({
              value: item.value,
              label: item.label,
              price: item.price,
              duration: item.duration,
              category: item.category,
            }))}
          />

          <Select
            withAsterisk
            label={t("appointment-purpose")}
            mt={"1rem"}
            placeholder={t("select-appointment-purpose")}
            data={[
              { label: t("general-consultation"), value: "General Consultation" },
              { label: t("fever-malaria"), value: "Fever / Malaria" },
              { label: t("cough-cold"), value: "Cough / Cold" },
              { label: t("skin-issue"), value: "Skin Issue" },
              { label: t("stomach-pain-diarrhea"), value: "Stomach Pain / Diarrhea" },
              { label: t("prescription-renewal"), value: "Prescription Renewal" },
              { label: t("need-advice"), value: "Need Advice" },
              { label: t("other"), value: "Other" },
            ]}
            {...form.getInputProps("additionalInformation")}
            key={form.key("additionalInformation")}
          />

          <Group
            align="end"
            w={{ base: "100%", sm: "calc(50% - 1rem)" }}
            mt={"1rem"}
            gap={5}>
            <TextInput
              disabled={isPromoCodeValid === "true"}
              label={t("promo-code")}
              flex={1}
              placeholder={t("eg", { example: "ABCD123" })}
              rightSection={
                <IconRosetteDiscount size={20} stroke={1.5} color="green" />
              }
              value={promoCode}
              onChange={e => setPromoCode(e.target.value as string)}
            />
            <Button
              disabled={!promoCode || isPromoCodeValid === "true"}
              loading={promoCodeFetcher.state === "submitting"}
              size="xs"
              variant="outline"
              type="button"
              h={36}
              onClick={() => {
                if (!promoCode) return;
                promoCodeFetcher.submit(null, {
                  method: "post",
                  action: `/api/validate-promo-code/${promoCode}`,
                });
              }}>
              {t("apply")}
            </Button>
          </Group>

          {isPromoCodeValid === "true" ? (
            <Alert
              mt="1rem"
              variant="light"
              color={"green"}
              radius={"sm"}
              icon={<IconDiscountCheckFilled />}
              // withCloseButton
              onClose={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"green"}>
                <Trans
                  i18nKey="promo-code-discount-x"
                  values={{ discountPercent }}
                  components={{ bold: <Text span inherit fw={600} /> }}
                />
              </Text>
            </Alert>
          ) : null}

          {isPromoCodeValid === "false" ? (
            <Alert
              withCloseButton
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              onClick={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("promo-code-invalid")}
              </Text>
            </Alert>
          ) : null}

          <Title order={2} fw={500} fz={18} mt={"3rem"}>
            {t("personal-details")}
          </Title>

          <TextInput
            mt={"1rem"}
            label={t("full-name")}
            placeholder={t("eg", { example: "John Doe" })}
            {...form.getInputProps("fullName")}
            key={form.key("fullName")}
            withAsterisk
          />

          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            {/* <DatePickerInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder={t("select-dob")}
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            /> */}
            <DateInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder="DD/MM/YYYY"
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            />
            <Select
              w={{ base: "100%", sm: "48%" }}
              label={t("gender")}
              placeholder={t("select-gender")}
              data={getGenderValues(t)}
              {...form.getInputProps("gender")}
              key={form.key("gender")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <Stack gap="0.25rem" w={"100%"}>
              {noWhatsappNumber ? (
                <Input.Wrapper
                  label={t("phone-number")}
                  w={{ base: "100%" }}
                  withAsterisk>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.phoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.phoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("phoneNumber")}
                    key={form.key("phoneNumber")}
                  />
                  <Input.Error mt={5}>{form.errors.phoneNumber}</Input.Error>
                </Input.Wrapper>
              ) : (
                <Input.Wrapper
                  label={t("whatsapp-phone-number")}
                  description={t("whatsapp-number-reason")}
                  withAsterisk
                  w={{ base: "100%" }}>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.whatsappPhoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.whatsappPhoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("whatsappPhoneNumber")}
                    key={form.key("whatsappPhoneNumber")}
                  />
                  <Input.Error mt={5}>
                    {form.errors.whatsappPhoneNumber}
                  </Input.Error>
                </Input.Wrapper>
              )}

              <Checkbox
                label={t("no-whatsapp-number")}
                size="xs"
                checked={noWhatsappNumber}
                onChange={e => setNoWhatsappNumber(e.currentTarget.checked)}
              />
            </Stack>
          </Group>

          {sourceId && <input type="hidden" name="sourceId" value={sourceId} />}

          <Alert
            mt="2rem"
            variant="light"
            color={"blue"}
            radius={"sm"}
            icon={<IconInfoCircle />}>
            <Text fz={14} lh={1.5} c={"blue"}>
              {t("asap-time-slot-note")}
            </Text>
          </Alert>

          {actionFetcher.data?.error && (
            <Alert
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              title={
                <Text c={"red"} fw={500} fz={14} lh={1.5}>
                  {t("oops")}
                </Text>
              }>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("create-appointment-error")}
              </Text>
            </Alert>
          )}

          <PhoneNumberVerificationModal
            whatsappPhoneNumber={
              form.values.whatsappPhoneNumber.trim().length > 3
                ? form.values.whatsappPhoneNumber
                : ""
            }
            phoneNumber={
              form.values.phoneNumber.trim().length > 3
                ? form.values.phoneNumber
                : ""
            }
            opened={phoneNumberVerificationModalOpened}
            close={closePhoneNumberVerificationModal}
            setIsAllowedToBook={setIsAllowedToBook}
            timeLeft={timeLeft}
            isComplete={isComplete}
            resetTimer={resetTimer}
          />

          <Button
            type="submit"
            radius={"xl"}
            w={"100%"}
            mt={"3rem"}
            loading={isSubmitting}>
            {t("confirm-appointment")}
          </Button>
          <Text fz={12} c="dimmed" fs={"italic"} mt={"0.5rem"} ta="center">
            <Trans i18nKey="agree-to-privacy-policy">
              By confirming your appointment, you agree to our{" "}
              <Anchor
                component={Link}
                target="_blank"
                to="https://docs.google.com/document/d/1KxHsr6HBjC6KN3ms_LdKcoCZYdIQOSN5/edit"
                inherit>
                privacy policy
              </Anchor>
              .
            </Trans>
          </Text>
        </Form>
      </div>
    </div>
  );
}

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  // const t = await i18nextServer.getFixedT(request);
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;
  const source = new URL(request.url).searchParams.get("source") ?? "";

  const currencyContext = await resolveCurrencyContext(request, true);
  const currency = currencyContext?.currency ?? "XOF";
  const fxRate = currencyContext?.fxRate ?? 1;

  // * NB: Translation doesn't work for meta tags after deployment
  const text =
    locale === "fr"
      ? "Parlez rapidement à un médecin en Afrique francophone"
      : "Talk quickly to a doctor in Francophone Africa";

  const title = `${text} • ${PAGE_TITLE}`;
  let description =
    locale === "fr"
      ? "Décrivez votre besoin, vérifiez votre numéro et confirmez une demande de consultation avec un médecin disponible sur La Ruche Health."
      : "Describe your need, verify your number, and confirm a consultation request with an available doctor on La Ruche Health.";

  try {
    const services = await getDoctorAsapServices();
    // console.log("services:", services);

    if (services.ok) {
      services.data = services.data.map((service: any) => ({
        ...service,
        originalPrice: service?.price ?? 0, // Store original XOF price
        price: (service?.price ?? 0) * fxRate, // Display converted price
      }));
    }

    description =
      locale === "fr"
        ? description +
          ` Services pour: ${services.data
            ?.map((s: any) => s.label)
            .join(", ")} et plus`
        : description +
          ` Services for: ${services.data
            ?.map((s: any) => s.label)
            .join(", ")} and more`;

    return json(
      { title, description, locale, services, origin, currency, source },
      {
        headers: {
          "Cache-Control": `private, max-age=3600, stale-while-revalidate=${
            3600 * 24 * 7
          }`, // Cache for 1 hour
        },
      }
    );
  } catch (error) {
    // Handle any unexpected errors outside of API responses
    console.error("Unexpected error", error);
    return json(
      {
        ok: false,
        title,
        description,
        locale,
        currency,
        origin,
        services: null,
        source,
        error: "Unexpected error occurred",
      },
      { status: 500 }
    );
  }
};

// Booking Success Info cookie
const cookie = createCookie(`_${LARUCHE_IDENTIFIER}_bsi`, {
  maxAge: 60 * 60 * 24, // 1 day
  path: "/", // Path where the cookie is accessible - Entire domain
  secure: process.env.NODE_ENV === "production",
  httpOnly: true,
  sameSite: "lax",
  secrets: [process.env.COOKIE_SECRET as string],
});

export const action = async ({ request, params }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const parseTouch = (field: string) => {
    const value = formData.get(field)?.toString();
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  };
  const partnerSlug = formData.get("partnerSlug")?.toString();
  const sourceId = formData.get("sourceId")?.toString();
  const fullName = formData.get("fullName")?.toString() || "";

  let partnerId = formData.get("partnerId")?.toString();
  if (!partnerId && partnerSlug) {
    const partnerRes = await getPartnerProfileBySlug(partnerSlug);
    partnerId = partnerRes?.data?.id ?? "";
  }

  // Determine medium based on source
  let medium = "website";
  if (sourceId) {
    if (sourceId.startsWith("PHARM_")) {
      medium = "pharmacy";
    } else if (sourceId.startsWith("KIOSK_")) {
      medium = "kiosk";
    } else {
      medium = "kiosk"; // default other partners to kiosk, or you could keep them as website
    }
  }

  const newForm = {
    category: formData.get("category")?.toString() ?? "teleconsultation",
    status: "pending",
    is_follow_up: false,
    priority: "asap",
    medium: medium,
    source_id: sourceId || null,
    language: formData.get("language")?.toString() || null,
    promo_code: formData.get("promoCode")?.toString() || null,
    patient_timezone: formData.get("patient_timezone")?.toString() || undefined,
    timezone_offset_minutes: new Date(
      formData.get("timeZone")!.toString()
    ).getTimezoneOffset(),

    ...(formData.get("isQueueSubmission")?.toString() === "true" && {
      doctor_assigned: formData.get("doctorAssigned")?.toString() ?? "",
    }),
    ...(formData.get("isQueueSubmission")?.toString() === "true" && {
      queued: "true",
    }),
    ...(partnerId && { partner_id: partnerId }),
    appointment_type: formData.get("category")?.toString() || null,
    attribution_token:
      formData.get("attribution_token")?.toString() || null,
    attribution_share_id:
      formData.get("attribution_share_id")?.toString() || null,
    attribution_channel:
      formData.get("attribution_channel")?.toString() || "unknown",
    attribution_session_id:
      formData.get("attribution_session_id")?.toString() || null,
    attribution_visitor_id:
      formData.get("attribution_visitor_id")?.toString() || null,
    analytics_first_touch: parseTouch("analytics_first_touch"),
    analytics_last_touch: parseTouch("analytics_last_touch"),
    analytics_consent:
      formData.get("analytics_consent")?.toString() !== "false",
    marketing_consent:
      formData.get("marketing_consent")?.toString() !== "false",
    analytics_journey_id:
      formData.get("analytics_journey_id")?.toString() || null,
    analytics_origin_platform:
      formData.get("analytics_origin_platform")?.toString() || null,
    analytics_entry_point:
      formData.get("analytics_entry_point")?.toString() || null,
    analytics_profile_id:
      formData.get("analytics_profile_id")?.toString() || null,
    analytics_service_id:
      formData.get("analytics_service_id")?.toString() || null,
  } as any;

  const whatsappPhoneNumber = cleanPhoneNumber(
    formData.get("whatsappPhoneNumber")?.toString()
  ) || null;

  // Split fullName into first_name and surname
  const nameParts = fullName.trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const surname = nameParts.length > 1 ? nameParts.slice(1).join(" ") : " ";

  const details = {
    phone_number:
      whatsappPhoneNumber ||
      cleanPhoneNumber(formData.get("phoneNumber")?.toString()) ||
      null,
    whatsapp_phone_number: whatsappPhoneNumber,
    first_name: firstName,
    surname: surname,
    dob: formData.get("dob")
      ? dayjs(formData.get("dob") as string).format("DD/MM/YYYY")
      : null,
    gender: formData.get("gender")?.toString() || null,
    email: null,
    // appointment_type: newForm.category,
    location: formData.get("location")?.toString() || null,
  } as any;

  if (newForm.category === "teleconsultation") {
    details.t_service_date = new Date().toISOString();
    details.t_service_type = formData.get("serviceTypeLabel")?.toString() || null;
    details.t_service_type_id = formData.get("serviceType")?.toString() || null;
    details.t_additional_health_info = formData.get("additionalInformation")?.toString() || null;
    details.t_cost = formData.get("cost")?.toString() || null;
  } else {
    details.service_date = new Date().toISOString();
    details.service_type = formData.get("serviceTypeLabel")?.toString() || null;
    details.service_type_id = formData.get("serviceType")?.toString() || null;
    details.additional_health_info = formData.get("additionalInformation")?.toString() || null;
    details.cost = formData.get("cost")?.toString() || null;
  }

  newForm.details = details;

  const cookiePayload = {
    category: "asap",
    serviceType: formData.get("serviceTypeLabel"),
    price: formData.get("cost"),
    duration: 0,
    discountPercent: formData.get("discountPercent"),
    email: formData.get("email"),
    phoneNumber:
      whatsappPhoneNumber ||
      cleanPhoneNumber(formData.get("phoneNumber")?.toString()),
    promoCode: +formData.get("discountPercent")!
      ? formData.get("promoCode")
      : "", // * If there is no discount don't use promo code
  } as any;

  try {
    const res = await api.post("appointments/add", newForm);
    cookiePayload.apptId = res.data._id;
    const cookieHeader = await cookie.serialize(cookiePayload);
    // console.log(res.data);
    return redirect(
      `${params?.lang ? `/${params.lang}` : ""}/payment?apptId=${res.data._id}`,
      {
        headers: {
          "Set-Cookie": cookieHeader,
        },
      }
    );
  } catch (error: any) {
    console.error("Error creating appointment:", error);

    // You can check if the error is an AxiosError
    if (isAxiosError(error)) {
      if (error.response) {
        // Handle the response error
        return json(
          { error: error.response.data?.detail ?? "An error occurred." },
          { status: error.response.status }
        );
      }
      // Handle other types of Axios errors (like network errors)
      return json({ error: "Network error." }, { status: 500 });
    }

    // Handle generic errors
    return json({ error: "Failed to create appointment." }, { status: 500 });
  }
};
